<script setup lang="ts">
import VChart from 'vue-echarts'
import { ChevronDownIcon } from '@heroicons/vue/24/outline'
import { useBarChartWidget } from '@/composables/widgets/useBarChartWidget'
import { useI18n } from '@/composable/useI18n'
import type { IWidget } from '@/interfaces/components/dashboard/widget'
import type { ISensor } from '@/interfaces/entities/sensor'
import { useCompanyDashboardStore } from '@/store/companyDashboard'
import { storeToRefs } from 'pinia'
import { computed, inject, ref, watch, type Ref } from 'vue'
import {
  type BarChartResolution,
  formatResolutionLabel,
  getAvailableResolutions,
  getDefaultResolution,
} from '@/utils/chart/bar-chart-resolution'
import { buildBarChartSourceKey } from '@/composables/widgets/useBarChartWidget'
import { resolveWidgetDataFields } from '@/utils/sensor-schema-fields'
import { indicatorSourceKeyFromWidget, isGroupWidget } from '@/utils/dashboard/widget-source'

const props = defineProps<{
  widget: IWidget
  sensor: ISensor | undefined
}>()

const { t } = useI18n()
const store = useCompanyDashboardStore()
const { period, barChartSources, indicatorSources } = storeToRefs(store)
const dashboardPollTick = inject<Ref<number>>('dashboardPollTick')
const { getOption } = useBarChartWidget()
const isGroup = computed(() => isGroupWidget(props.widget))

const barChartLoading = computed(() => {
  if (isGroup.value) return store.widgetData[props.widget.id]?.loading ?? false
  return store.barChartWidgetStates[props.widget.id]?.loading ?? false
})
const barChartError = computed(() => {
  if (isGroup.value) return store.widgetData[props.widget.id]?.error ?? false
  return store.barChartWidgetStates[props.widget.id]?.error ?? false
})

const selectedResolution = ref<BarChartResolution>('15m')

const durationMs = computed(() => {
  if (!period.value) return 0
  return period.value[1].getTime() - period.value[0].getTime()
})

const availableResolutions = computed(() => getAvailableResolutions(durationMs.value))

const chartOption = computed(() => getOption(props.widget, selectedResolution.value))

const dataFields = computed(() => resolveWidgetDataFields(props.widget.config))

const hasChartData = computed(() => {
  if (isGroup.value) {
    return dataFields.value.some((dataField) => {
      const key = indicatorSourceKeyFromWidget(props.widget, dataField)
      if (!key) return false
      const aggregations = indicatorSources.value[key]?.aggregations
      return aggregations?.last != null || aggregations?.mean != null
    })
  }

  const sensorId = props.widget.config.sensor_ids?.[0]
  if (!sensorId) return false

  return dataFields.value.some((dataField) => {
    const key = buildBarChartSourceKey(selectedResolution.value, sensorId, dataField)
    const source = barChartSources.value[key]
    return source?.points?.some((point) => point.v != null) ?? false
  })
})

function syncResolutionFromPeriod() {
  if (durationMs.value <= 0) return

  const available = availableResolutions.value
  if (!available.includes(selectedResolution.value)) {
    selectedResolution.value = getDefaultResolution(durationMs.value)
  }
}

async function fetchData() {
  if (!period.value || durationMs.value <= 0) return
  if (isGroup.value) {
    await store.fetchAllIndicatorsData()
    return
  }
  await store.fetchBarChartData(selectedResolution.value, props.widget.id)
}

function onResolutionChange(event: Event) {
  const value = (event.target as HTMLSelectElement).value as BarChartResolution
  selectedResolution.value = value
  void fetchData()
}

watch(
  period,
  () => {
    if (!isGroup.value) syncResolutionFromPeriod()
    void fetchData()
  },
  { immediate: true, deep: true },
)

watch(
  () => [
    props.widget.config.sensor_ids,
    props.widget.config.group_ids,
    props.widget.config.data_fields,
    props.widget.config.data_field,
    props.widget.config.aggregation,
  ],
  () => {
    void fetchData()
  },
  { deep: true },
)

if (dashboardPollTick) {
  watch(dashboardPollTick, () => {
    void fetchData()
  })
}
</script>

<template>
  <div class="flex h-full min-h-0 w-full flex-col">
    <div
      v-if="!isGroup"
      class="mb-1 flex shrink-0 flex-nowrap items-center justify-end gap-2 px-1"
    >
      <label class="shrink-0 text-2xs font-medium uppercase tracking-widest text-gray-500">
        {{ t('custom_dashboards.view.bar_chart.resolution_label') }}
      </label>
      <div class="relative inline-flex shrink-0 items-center">
        <select
          :value="selectedResolution"
          class="appearance-none border border-stroke bg-gray-2 py-2 pl-4 pr-7 text-xs font-medium text-black outline-none focus:border-primary"
          @change="onResolutionChange"
        >
          <option
            v-for="resolution in availableResolutions"
            :key="resolution"
            :value="resolution"
          >
            {{ formatResolutionLabel(resolution, t) }}
          </option>
        </select>
        <ChevronDownIcon
          class="pointer-events-none absolute right-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400"
        />
      </div>
    </div>

    <div v-if="barChartLoading && !hasChartData" class="flex flex-1 items-center justify-center text-sm text-body">
      {{ t('custom_dashboards.view.bar_chart.loading') }}
    </div>

    <div v-else-if="barChartError" class="flex flex-1 items-center justify-center text-sm text-red">
      {{ t('custom_dashboards.view.bar_chart.error') }}
    </div>

    <div v-else-if="!hasChartData" class="flex flex-1 items-center justify-center text-sm text-body">
      {{ t('custom_dashboards.view.bar_chart.empty') }}
    </div>

    <VChart
      v-else
      :option="chartOption"
      class="h-full min-h-0 w-full flex-1"
      autoresize
    />
  </div>
</template>
